import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import { installPlatformShim } from './platform';
import App from './App';
import './index.css';

type AppErrorBoundaryState = { error: Error | null };

const reportClientError = (error: Error, source: string): void => {
  try {
    const token = localStorage.getItem('boclaw_token');
    const userId = localStorage.getItem('boclaw_user_id') || '';
    void fetch('/api/app/client-error', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(userId ? { 'X-Boclaw-User-Id': userId } : {}),
      },
      body: JSON.stringify({
        source,
        message: error.message.slice(0, 1000),
        stack: error.stack?.slice(0, 6000) || '',
        path: window.location.pathname,
      }),
    }).catch(() => {});
  } catch {
    // Error reporting must never trigger a second UI failure.
  }
};

class AppErrorBoundary extends React.Component<React.PropsWithChildren, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error): void {
    console.error('[UI] Unhandled render error:', error);
    reportClientError(error, 'react-error-boundary');
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-screen items-center justify-center bg-white px-6 text-gray-900">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-red-50 p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold">页面运行异常</h1>
          <p className="mt-2 text-sm text-gray-600">错误已自动上报，请刷新页面重试。</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            刷新页面
          </button>
        </div>
      </div>
    );
  }
}

// Install platform shim before any service initialization
installPlatformShim();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

try {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Provider store={store}>
        <AppErrorBoundary>
          <App />
        </AppErrorBoundary>
      </Provider>
    </React.StrictMode>
  );
} catch (error) {
  console.error('Failed to render the app:', error);
}
